import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { Container, Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getSiteContent } from '../../services/storage';
import { DEFAULT_CONTENT, toSlug } from './UeberUnsPage';
import type { TeamMember } from './UeberUnsPage';

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: 'easeOut' },
  }),
};

function getInitials(name: string): string {
  return name.split(' ').map((p) => p.charAt(0)).slice(0, 2).join('').toUpperCase();
}

const TeamMemberPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [member, setMember] = useState<TeamMember | null>(
    (location.state as { member?: TeamMember })?.member ?? null
  );
  const [loading, setLoading] = useState(!member);

  useEffect(() => {
    if (member) return;
    getSiteContent('about_us')
      .then((data) => {
        const content = (data && Object.keys(data).length > 0 ? data : DEFAULT_CONTENT) as unknown as typeof DEFAULT_CONTENT;
        const found = content.team.find((m) => toSlug(m.name) === slug) ?? null;
        setMember(found);
      })
      .catch(() => {
        const found = DEFAULT_CONTENT.team.find((m) => toSlug(m.name) === slug) ?? null;
        setMember(found);
      })
      .finally(() => setLoading(false));
  }, [slug, member]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: '#888' }}>Laden…</Typography>
      </Box>
    );
  }

  if (!member) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0a0a1a' }}>Teammitglied nicht gefunden</Typography>
        <Button component={RouterLink} to="/ueber-uns" startIcon={<ArrowBackIcon />} sx={{ color: '#1737bc' }}>
          Zurück zum Team
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>

      {/* ── Dark hero header ── */}
      <Box
        sx={{
          bgcolor: '#0a0a1a',
          pt: { xs: 14, md: 16 },
          pb: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle background glow */}
        <Box
          sx={{
            position: 'absolute',
            top: -120,
            left: -80,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(23,55,188,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <MotionBox initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <Button
              component={RouterLink}
              to="/ueber-uns"
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.82rem',
                fontWeight: 500,
                mb: 5,
                px: 0,
                '&:hover': { color: '#fff', bgcolor: 'transparent' },
              }}
            >
              Zurück zum Team
            </Button>
          </MotionBox>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 10 }, alignItems: { md: 'center' } }}>
            {/* Photo */}
            <MotionBox initial="hidden" animate="visible" custom={1} variants={fadeUp} sx={{ flexShrink: 0 }}>
              {member.image ? (
                <Box
                  component="img"
                  src={member.image}
                  alt={member.name}
                  sx={{
                    width: { xs: 140, md: 200 },
                    height: { xs: 140, md: 200 },
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '4px solid rgba(255,255,255,0.1)',
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: { xs: 140, md: 200 },
                    height: { xs: 140, md: 200 },
                    borderRadius: '50%',
                    bgcolor: '#1737bc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '4px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 800, color: 'rgba(255,255,255,0.3)' }}>
                    {getInitials(member.name)}
                  </Typography>
                </Box>
              )}
            </MotionBox>

            {/* Name + role */}
            <Box>
              <MotionBox initial="hidden" animate="visible" custom={2} variants={fadeUp}>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: '#818cf8',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Unser Team
                </Typography>
              </MotionBox>
              <MotionBox initial="hidden" animate="visible" custom={3} variants={fadeUp}>
                <Typography
                  variant="h1"
                  sx={{ fontSize: { xs: '2rem', md: '3.5rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.1, mb: 2 }}
                >
                  {member.name}
                </Typography>
              </MotionBox>
              <MotionBox initial="hidden" animate="visible" custom={4} variants={fadeUp}>
                <Typography sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.45)', fontWeight: 400 }}>
                  {member.role}
                </Typography>
              </MotionBox>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Bio ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 720 }}>
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
            variants={fadeUp}
          >
            <Typography
              variant="overline"
              sx={{ color: '#1737bc', letterSpacing: '3px', fontWeight: 700, display: 'block', mb: 3 }}
            >
              Über {member.name.split(' ')[0]}
            </Typography>
            {(member.desc || '').split('\n\n').map((para, i) => (
              <Typography key={i} sx={{ color: '#444', fontSize: '1.05rem', lineHeight: 1.9, mb: 3 }}>
                {para}
              </Typography>
            ))}
          </MotionBox>

          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            sx={{ mt: 6, display: 'flex', gap: 2, flexWrap: 'wrap' }}
          >
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              sx={{
                bgcolor: '#1737bc',
                color: '#fff',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': { bgcolor: '#0f2899' },
              }}
            >
              Kontakt aufnehmen
            </Button>
            <Button
              component={RouterLink}
              to="/ueber-uns"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{
                borderColor: '#1737bc',
                color: '#1737bc',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': { bgcolor: '#1737bc', color: '#fff' },
              }}
            >
              Alle Teammitglieder
            </Button>
          </MotionBox>
        </Box>
      </Container>

    </Box>
  );
};

export default TeamMemberPage;
